// utils/deliveryCalculator.js
// Utility function to calculate delivery costs based on weight and size

// Helper function to determine if an item can be rolled for delivery
const canBeRolledForDelivery = (item) => {
  const rollableJobTypes = [
    "banner",
    "poster",
    "cutvinyl",
    "cut vinyl",
    "roller-banner",
    "vinyl-stickers",
    "vinyl stickers",
    "print-cut",
    "print & cut",
  ];
  const jobType = item.jobType || "";
  return rollableJobTypes.some((rollableType) =>
    jobType.toLowerCase().includes(rollableType.toLowerCase()),
  );
};

// Helper function to get packaged dimensions for delivery
const getPackagedDimensions = (item) => {
  const width = item.width || 0;
  const height = item.height || 0;

  if (canBeRolledForDelivery(item)) {
    // For rollable items, use the shorter dimension (rolled dimension)
    return {
      longestDimension: Math.min(width, height),
      shortestDimension: Math.max(width, height),
      isRolled: true,
    };
  } else {
    // For non-rollable items, use the longer dimension
    return {
      longestDimension: Math.max(width, height),
      shortestDimension: Math.min(width, height),
      isRolled: false,
    };
  }
};

export const calculateDeliveryCost = (
  quoteItems,
  deliveryType,
  expressSubOption = null,
) => {
  console.log("=== Delivery Calculation Debug ===");
  console.log("Quote Items:", JSON.stringify(quoteItems, null, 2));

  // Calculate total weight from all items
  const totalWeight = quoteItems.reduce((sum, item) => {
    return sum + (item.breakdown?.total_weight_kg || estimateWeight(item));
  }, 0);

  // Count boxes needed (assume 1 box per 10kg, minimum 1)
  const boxesNeeded = Math.max(1, Math.ceil(totalWeight / 10));

  // Check for oversized items (longer than 1m in any dimension)
  // For rollable items, use the shorter dimension (rolled dimension)
  const oversizedItems = [];
  const hasOversizedItems = quoteItems.some((item) => {
    const packagedDimensions = getPackagedDimensions(item);
    const isOversized = packagedDimensions.longestDimension > 1000;

    console.log("Item check:", {
      jobType: item.jobType,
      width: item.width,
      height: item.height,
      packagedDimensions,
      isOversized,
    });

    if (isOversized) {
      oversizedItems.push({
        jobType: item.jobType,
        originalDimensions: {
          width: item.width || 0,
          height: item.height || 0,
        },
        packagedDimensions,
        isRolled: packagedDimensions.isRolled,
      });
    }

    return isOversized;
  });

  // Base pricing
  let cost = 0;
  let expressSubOptionCost = 0;

  // Express delivery: £9.50 per box + sub-option surcharges
  if (deliveryType === "express") {
    cost = boxesNeeded * 9.5;

    // Add express sub-option surcharges
    if (expressSubOption) {
      switch (expressSubOption) {
        case "pre12":
          expressSubOptionCost = boxesNeeded * 6; // +£6 per box
          break;
        case "pre1030":
          expressSubOptionCost = boxesNeeded * 10; // +£10 per box
          break;
        case "saturday1030":
          expressSubOptionCost = boxesNeeded * 18; // +£18 per box
          break;
      }
      cost += expressSubOptionCost;
    }
  }

  // Multi-address delivery: handled in QuoteReview component
  // This function is primarily for express delivery calculation

  // Add £20 surcharge for oversized items
  if (hasOversizedItems) {
    cost += 20;
  }

  return {
    cost,
    boxesNeeded,
    hasOversizedItems,
    estimatedWeight: parseFloat(totalWeight.toFixed(2)),
    oversizedItems, // Include details about which items are oversized and how they were processed
    expressSubOption,
    expressSubOptionCost,
  };
};

// Helper to estimate weight for items without weight data
const estimateWeight = (item) => {
  if (!item) return 0;

  // For items with breakdown that has weight info
  if (item.breakdown?.total_weight_kg_combined) {
    return item.breakdown.total_weight_kg_combined;
  }

  // For items with paperUsage that has weight info (small format)
  if (item.paperUsage?.total_weight_kg_combined) {
    return item.paperUsage.total_weight_kg_combined;
  }

  // Fallback estimation based on product type and dimensions
  if (
    item.productType === "large-format" ||
    item.productType === "large_format"
  ) {
    // Convert dimensions from mm to m²
    const width_m = (item.width || 0) / 1000;
    const height_m = (item.height || 0) / 1000;
    const area_sqm = width_m * height_m;

    // Estimate based on material type
    let weightPerSqm = 0.25; // Default: 0.25kg per square meter

    // Adjust based on material
    if (item.material) {
      const material = item.material.toLowerCase();
      if (material.includes("banner") || material.includes("vinyl")) {
        weightPerSqm = 0.4; // Lighter materials
      } else if (material.includes("board") || material.includes("foam")) {
        weightPerSqm = 1.2; // Heavier materials
      }
    }

    return area_sqm * weightPerSqm * (item.quantity || 1);
  } else if (
    item.productType === "small_format" ||
    item.productType === "small-format"
  ) {
    // For small format, estimate based on paper weight and dimensions
    const width_m = (item.width || 0) / 1000;
    const height_m = (item.height || 0) / 1000;
    const area_sqm = width_m * height_m;

    // Booklets are heavier
    if (item.isBooklet) {
      const pageCount = item.pageCount || 4;
      return ((area_sqm * 0.09 * pageCount) / 4) * (item.quantity || 1); // 90g per m² per 4 pages
    }

    // Regular print
    return area_sqm * 0.2 * (item.quantity || 1); // 200g per m²
  }

  // Default fallback
  return 0.5 * (item.quantity || 1); // Default 0.5kg per item
};
